import styled from '@emotion/styled';
import { Label, Button, FormDatePicker, FormTimePicker } from '@britishcouncil/react-admin';
import { Timestamp, TimestampType } from '@britishcouncil/common-core';
import {
  LrwSessionSalesChannelAvailabilityDto,
  UpdateSalesChannelAvailabilityRequest,
} from 'api/planning';
import { useEffect, useMemo, useState } from 'react';
import { Dropdown, Message, Table } from 'semantic-ui-react';
import { lrwSessionStore } from 'store';
import { useIntParam, useLrwSessionAllowedActions, useSafeMutation, useServerErrors } from 'hooks';
import { parseServerErrors } from 'utils';

type Props = {
  salesChannelAvailability: LrwSessionSalesChannelAvailabilityDto;
};

type SaleChannelRow = {
  id: number;
  name: string;
  available: boolean;
  closingDate: TimestampType | null;
  closingTime: string;
};

export const SalesChannelAvailabilityTable = ({ salesChannelAvailability }: Props) => {
  const lrwSessionId = useIntParam('sessionId');
  const [rows, setRows] = useState<SaleChannelRow[]>([]);
  const [baselineRows, setBaselineRows] = useState<SaleChannelRow[]>([]);
  const visibleRows = rows.filter(x => !(x.name === 'Organisational' && !x.closingDate));
  const { canUpdateSalesChannelAvailability } = useLrwSessionAllowedActions();

  const [updateSaleChannelAvailability, { isLoading, error }] =
    lrwSessionStore.service.useUpdateSalesChannelAvailabilityMutation();

  const { execute: saveSaleChannelAvailability } = useSafeMutation(updateSaleChannelAvailability, {
    successToastMessage: 'Sale channel availability updated successfully.',
    onSuccess: () => setBaselineRows(rows),
  });

  const serverErrors = useMemo(() => parseServerErrors(error), [error]);

  const { generalErrors, clearServerErrors } = useServerErrors({ rows }, serverErrors);

  const mappedRows = useMemo(() => mapRows(salesChannelAvailability), [salesChannelAvailability]);

  const isEditable =
    canUpdateSalesChannelAvailability && salesChannelAvailability.areChannelsEditable;

  useEffect(() => {
    setRows(mappedRows);
    setBaselineRows(mappedRows);
  }, [mappedRows]);

  const hasChanges = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(baselineRows),
    [rows, baselineRows]
  );

  const handleAvailableChange = (id: number, value: boolean) => {
    setRows(prev => prev.map(row => (row.id === id ? { ...row, available: value } : row)));
  };

  const handleDateChange = (id: number, value: TimestampType | null) => {
    setRows(prev => prev.map(row => (row.id === id ? { ...row, closingDate: value } : row)));
  };

  const handleTimeChange = (id: number, value: string) => {
    setRows(prev => prev.map(row => (row.id === id ? { ...row, closingTime: value } : row)));
  };

  const handleCancel = () => {
    clearServerErrors();
    setRows(baselineRows);
  };

  const handleSave = () => {
    clearServerErrors();

    const request = buildUpdateRequest(rows);

    saveSaleChannelAvailability({
      lrwSessionId,
      request,
    });
  };

  const dropdownOptions = [
    { key: 'yes', text: 'Yes', value: true },
    { key: 'no', text: 'No', value: false },
  ];

  return (
    <S.TableWrapper>
      {generalErrors.length > 0 && (
        <S.ErrorsWrapper>
          <Message
            warning
            list={generalErrors.map((x, index) => ({
              key: index,
              content: x.message,
            }))}
          />
        </S.ErrorsWrapper>
      )}

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={4}>Sale Channel</Table.HeaderCell>
            <Table.HeaderCell width={3}>Available</Table.HeaderCell>
            <Table.HeaderCell width={5}>Closing date</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {visibleRows.map(row => (
            <Table.Row key={row.id}>
              <S.ChannelCell verticalAlign="middle">
                <S.ChannelLabelWrapper>
                  <Label basic={false} color={getChannelColor(row.name)} size="large">
                    <S.ChannelLabelText>{row.name}</S.ChannelLabelText>
                  </Label>
                </S.ChannelLabelWrapper>
              </S.ChannelCell>

              <Table.Cell verticalAlign="middle">
                <S.DropdownWrapper>
                  <Dropdown
                    fluid
                    selection
                    options={dropdownOptions}
                    value={row.available}
                    disabled={!isEditable}
                    onChange={(_, data) => handleAvailableChange(row.id, data.value as boolean)}
                  />
                </S.DropdownWrapper>
              </Table.Cell>

              <Table.Cell verticalAlign="middle">
                <S.DateTimeWrapper disabled={!isEditable}>
                  <DateField
                    value={row.closingDate}
                    disabled={!isEditable}
                    onChange={value => handleDateChange(row.id, value)}
                  />
                  <TimeField
                    value={row.closingTime}
                    disabled={!isEditable}
                    onChange={value => handleTimeChange(row.id, value)}
                  />
                </S.DateTimeWrapper>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {canUpdateSalesChannelAvailability && (
        <S.Actions>
          <Button
            size="small"
            color="grey"
            onClick={handleCancel}
            disabled={isLoading || !hasChanges}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="green"
            onClick={handleSave}
            loading={isLoading}
            disabled={isLoading || !hasChanges}
          >
            Save
          </Button>
        </S.Actions>
      )}
    </S.TableWrapper>
  );
};

type DateFieldProps = {
  value: TimestampType | null;
  onChange: (value: TimestampType | null) => void;
  disabled?: boolean;
};

type TimeFieldProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const DateField = ({ value, onChange, disabled }: DateFieldProps) => {
  return (
    <S.DatePickerWrapper>
      <FormDatePicker
        name="closingDate"
        label=""
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </S.DatePickerWrapper>
  );
};

const TimeField = ({ value, onChange, disabled }: TimeFieldProps) => {
  return (
    <S.TimePickerWrapper>
      <FormTimePicker
        name="closingTime"
        label=""
        icon="clock outline"
        value={value}
        onChange={onChange}
        disabled={disabled}
        precision={30}
      />
    </S.TimePickerWrapper>
  );
};

const mapRows = (
  saleChannelAvailability: LrwSessionSalesChannelAvailabilityDto
): SaleChannelRow[] => [
  mapRow(1, 'Admin', saleChannelAvailability.adminAvailable, saleChannelAvailability.adminClosingDate),
  mapRow(2, 'B2C', saleChannelAvailability.b2CAvailable, saleChannelAvailability.b2CClosingDate),
  mapRow(3, 'B2B', saleChannelAvailability.b2BAvailable, saleChannelAvailability.b2BClosingDate),
  mapRow(
    4,
    'Organisational',
    saleChannelAvailability.organisationalAvailable ?? false,
    saleChannelAvailability.organisationalClosingDate ?? ''
  ),
];

const buildUpdateRequest = (rows: SaleChannelRow[]): UpdateSalesChannelAvailabilityRequest => {
  const adminRow = rows.find(row => row.name === 'Admin');
  const b2cRow = rows.find(row => row.name === 'B2C');
  const b2bRow = rows.find(row => row.name === 'B2B');
  const organisationalRow = rows.find(row => row.name === 'Organisational');

  return {
    adminAvailable: adminRow?.available ?? false,
    adminClosingDate: toUtcIsoString(adminRow) ?? '',
    b2CAvailable: b2cRow?.available ?? false,
    b2CClosingDate: toUtcIsoString(b2cRow) ?? '',
    b2BAvailable: b2bRow?.available ?? false,
    b2BClosingDate: toUtcIsoString(b2bRow) ?? '',
    organisationalAvailable: organisationalRow?.available ?? false,
    organisationalClosingDate: toUtcIsoString(organisationalRow),
  };
};

const mapRow = (
  id: number,
  name: string,
  available: boolean,
  closingDateUtc: string
): SaleChannelRow => {
  const { date, time } = splitDateTime(closingDateUtc);

  return {
    id,
    name,
    available,
    closingDate: date,
    closingTime: time,
  };
};

const splitDateTime = (value: string): { date: TimestampType | null; time: string } => {
  if (!value) {
    return { date: null, time: '' };
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return { date: null, time: '' };
  }

  return {
    date: parsed.getTime() as TimestampType,
    time: formatTimeAsUtc(parsed),
  };
};

const formatTimeAsUtc = (date: Date): string => {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(date);
};

const toUtcIsoString = (row?: SaleChannelRow): string | undefined => {
  if (!row?.closingDate || !row.closingTime) {
    return undefined;
  }

  const [hoursStr, minutesStr] = row.closingTime.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return undefined;
  }

  const dayStart = Timestamp.startOfDay(row.closingDate);
  const withTime = Timestamp.addMinutes(minutes)(Timestamp.addHours(hours)(dayStart));

  return Timestamp.toISOString(withTime);
};

const getChannelColor = (name: string) => {
  switch (name) {
    case 'Admin':
      return 'red';
    case 'B2C':
      return 'blue';
    case 'B2B':
      return 'grey';
    default:
      return 'teal';
  }
};

const S = {
  TableWrapper: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: visible;
    padding-bottom: 400px;
  `,

  ErrorsWrapper: styled.div`
    margin-bottom: 16px;
  `,

  DropdownWrapper: styled.div`
    .ui.selection.dropdown {
      z-index: 1;
    }

    .ui.selection.dropdown.active,
    .ui.selection.dropdown.visible {
      z-index: 20;
    }

    .ui.selection.dropdown .menu {
      z-index: 30;
    }
  `,

  ChannelCell: styled(Table.Cell)`
    text-align: center;
  `,

  ChannelLabelWrapper: styled.div`
    display: flex;
    justify-content: center;
  `,

  ChannelLabelText: styled.span`
    display: inline-block;
    min-width: 200px;
    text-align: center;
  `,

  DateTimeWrapper: styled.div<{ disabled: boolean }>`
    display: flex;
    align-items: baseline;
    gap: 8px;
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  `,

  DatePickerWrapper: styled.div`
    min-width: 120px;
  `,

  TimePickerWrapper: styled.div<{ disabled?: boolean }>`
    min-width: 180px;

    .ui.input,
    .ui.input input,
    input,
    .icon,
    .dropdown.icon {
      cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')} !important;
    }
  `,

  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
  `,
};
